import _ from 'lodash';

import { AttributeDefinitionValueListItem } from 'src/app/shared/models/attributes/attributeDefinitionValueListItem';
import { AttributeUiType } from 'src/app/shared/models/attributes/attributeUiType';
import { AttributeUiTypeDetails } from 'src/app/shared/models/attributes/attributeUiTypeDetails';
import { AttributeEntityType } from './attributeEntityType';
import { AttributeEntityTypeMetadata } from 'src/app/admin/admin-home/custom-fields/attributeEntityTypeMetadata';
import { AttributeDefinitionCondition } from './attributeDefinitionCondition';
import { AttributeDefinitionWorkspace } from '../workspace/attributeDefinitionWorkspace';
import { AttributeDefinitionSubFieldItem } from './attributeDefinitionSubFieldItem';
import { AttributeDefinitionWrite } from './attributeDefinitionWrite';
import { SearchValueType } from '../../components/odata-filter/models/search-value-type';

export class AttributeDefinition {
  public id: string;
  public entityType: string;
  public uiType: string;
  /**
   * The "effective" UI type for filtering / display decisions. Equals `uiType` for non-calculated
   * attributes; for calculated fields it maps from FormulaReturnType to the analogous UIType
   * (Text→ShortAnswer, Enumerated→DropDown, Number→Number, Date→Date). Optional: falls back to
   * `uiType` when absent (older response payloads, in-memory test fixtures, etc.).
   */
  public effectiveType?: string;
  /**
   * For calculated fields: the JSON expression tree the backend evaluator runs to produce
   * materialized values. Null/undefined for non-calculated fields. Only populated on the admin
   * read path (AttributeController GET) — not present on portal-facing payloads where the
   * formula isn't user-relevant.
   */
  public formulaExpression?: string;
  /** For calculated fields: the formula's return type (Text/Enumerated/Number/Date). */
  public formulaReturnType?: string;
  public name: string;
  public description: string;
  public adminDocumentation: string;
  public formPageId: string;
  public filterable: boolean;
  public featured: boolean;
  public required: boolean;
  public isReadOnly: boolean;
  public isHidden: boolean;
  public isArchived: boolean = false;
  public hasOther: boolean = false;
  public hasVisualization: boolean = false;
  public includeInRatingCalculation: boolean = true;
  public showOnCreate: boolean;
  public isSubField: boolean;
  public min: string;
  public max: string;
  public allowsUserAddedValues: boolean;
  public valueList: AttributeDefinitionValueListItem[];
  public subFieldList: AttributeDefinitionSubFieldItem[];
  public conditions: AttributeDefinitionCondition[];
  public workspaces: AttributeDefinitionWorkspace[];
  createdBy: string;
  createdOn: Date;
  updatedBy: string;
  updatedOn: Date;
  slug: string;
  /**
   * Whether the current (encrypted) value can be shown/revealed in this form context. Only
   * meaningful for form-fill contexts (Application/Survey/Evaluation questions); left undefined
   * for Person/Team profile AttributeDefinitions, which don't need it since reveal there is
   * gated by isProfileEditorMode() alone.
   */
  public showCurrentValue?: boolean;

  constructor(
    id: string,
    entityType: string,
    uiType: string,
    name: string,
    slug: string,
    description: string,
    adminDocumentation: string,
    filterable: boolean,
    featured: boolean,
    required: boolean,
    isReadOnly: boolean,
    isHidden: boolean,
    hasVisualization: boolean,
    showOnCreate: boolean,
    isSubField: boolean,
    min: string,
    max: string,
    allowsUserAddedValues: boolean,
    valueListParam: AttributeDefinitionValueListItem[],
    subFieldListParam: AttributeDefinitionSubFieldItem[],
    workspaces: AttributeDefinitionWorkspace[],
    createdBy: string,
    createdOn: Date,
    updatedBy: string,
    updatedOn: Date,
    isArchived: boolean = false,
    hasOther: boolean = false,
    includeInRatingCalculation: boolean = true
  ) {
    const attributeDefinitionValueListItems = valueListParam?.length
      ? valueListParam
          .map((attributeDefinitionValueListItem) => {
            return AttributeDefinitionValueListItem.parse(attributeDefinitionValueListItem);
          })
          .sort((a, b) => a.rank - b.rank)
      : [];

    const subFieldList = subFieldListParam
      ?.map((subField) => {
        return AttributeDefinitionSubFieldItem.parse(subField);
      })
      .sort((a, b) => a.rank - b.rank);

    this.id = id;
    this.entityType = entityType;
    this.uiType = uiType;
    this.name = name;
    this.slug = slug;
    this.description = description;
    this.adminDocumentation = adminDocumentation;
    this.filterable = this.canBeFiltered ? filterable : false;
    this.featured = featured;
    this.required = required;
    this.isReadOnly = isReadOnly;
    this.isHidden = isHidden;
    this.hasVisualization = hasVisualization;
    this.showOnCreate = showOnCreate;
    this.isSubField = isSubField;
    this.min = min;
    this.max = max;
    this.allowsUserAddedValues = allowsUserAddedValues;
    this.valueList = attributeDefinitionValueListItems;
    this.subFieldList = subFieldList;
    this.workspaces = workspaces;
    this.createdBy = createdBy;
    this.createdOn = createdOn;
    this.updatedBy = updatedBy;
    this.updatedOn = updatedOn;
    this.isArchived = isArchived;
    this.hasOther = hasOther;
    this.includeInRatingCalculation = includeInRatingCalculation;
  }

  get allowsMultipleValues(): boolean {
    return this.isOfUiType([AttributeUiType.Checkboxes, AttributeUiType.MultiSelect]);
  }

  isCheckboxType(): boolean {
    return this.isOfUiType([AttributeUiType.Checkboxes]);
  }

  get hasValueList(): boolean {
    return this.valueList && !_.isEmpty(this.valueList);
  }

  get isEnumerated(): boolean {
    return this.uiTypeDetails.isEnumerated;
  }

  get isSupportedForSubFieldItems(): boolean {
    return this.uiTypeDetails.isSupportedForSubFieldItems && this.isSubField;
  }

  get hasDescription(): boolean {
    return this.entityTypeMetadata.hasDescription;
  }

  public isOfUiType(uiTypes: Array<AttributeUiType>): boolean {
    return _.some(uiTypes, (uiType) => uiType == this.uiType);
  }

  public getValueListItem(value: string) {
    let item;
    item = _.find(this.valueList, { id: value });

    return item ? item : _.find(this.valueList, { itemValue: value });
  }

  public moveValueListItem(oldIndex: number, newIndex: number) {
    let movedValue = this.valueList.splice(oldIndex, 1);
    this.valueList.splice(newIndex, 0, ...movedValue);
    this.valueList.map((listItem, index) => {
      listItem.rank = index + 1;
    });
  }

  public getIcon(): string {
    return this.uiTypeDetails.icon;
  }

  public getUiName(): string {
    return this.uiTypeDetails.name;
  }

  get supportsOtherValue(): boolean {
    return this.uiTypeDetails.supportsOtherValue && this.hasOther;
  }

  private get uiTypeDetails(): AttributeUiTypeDetails {
    return new AttributeUiTypeDetails(this.uiType as AttributeUiType);
  }

  private get entityTypeMetadata(): AttributeEntityTypeMetadata {
    return AttributeEntityTypeMetadata.get(this.entityType as AttributeEntityType);
  }

  private get canBeFiltered(): boolean {
    return new AttributeUiTypeDetails(
      (this.effectiveType ?? this.uiType) as AttributeUiType
    ).canBeFiltered();
  }

  public buildCloneModel(): AttributeDefinitionWrite {
    let writeModel = new AttributeDefinitionWrite();
    var entityType = AttributeEntityType[this.entityType];
    var uiType = AttributeUiType[this.uiType];
    writeModel.hasOther = this.hasOther;
    writeModel.entityType = entityType;
    writeModel.uiType = uiType;
    writeModel.name = `${this.name} (copy)`;
    writeModel.description = this.description;
    writeModel.adminDocumentation = this.adminDocumentation;
    writeModel.isSubField = this.isSubField;
    if (this.isEnumerated && entityType === AttributeEntityType.Person) {
      writeModel.featured = this.featured;
      writeModel.hasVisualization = this.hasVisualization;
    }
    writeModel.filterable = this.filterable;
    if (entityType === AttributeEntityType.Team && uiType !== AttributeUiType.Document) {
      writeModel.showOnCreate = this.showOnCreate;
    }
    if (uiType === AttributeUiType.StarRating && entityType === AttributeEntityType.Evaluation) {
      writeModel.includeInRatingCalculation = this.includeInRatingCalculation;
    }
    writeModel.required = this.required;
    writeModel.valueList = [];

    writeModel.valueList = this.valueList.map((x) => {
      return {
        itemValue: x.itemValue,
        itemDescription: x.itemDescription,
        rank: x.rank
      } as AttributeDefinitionValueListItem;
    });

    if (uiType == AttributeUiType.Number) {
      writeModel.min = this.min;
      writeModel.max = this.max;
    }

    // Checkboxes carries the max-selections limit in max (no min); preserve it on clone
    if (uiType === AttributeUiType.Checkboxes) {
      writeModel.max = this.max;
    }

    if (uiType === AttributeUiType.Complex) {
      writeModel.min = this.min;
      writeModel.max = this.max;

      writeModel.subFieldList = this.subFieldList.map((x) => {
        return {
          subFieldDefinitionId: x.subFieldDefinitionId,
          subFieldDescription: x.subFieldDescription,
          rank: x.rank,
          required: x.required,
          isHiddenOnForms: x.isHiddenOnForms
        } as AttributeDefinitionSubFieldItem;
      });
    }

    return writeModel;
  }

  static parse(obj: Partial<AttributeDefinition>): AttributeDefinition {
    if (!obj) return null;
    const result = new AttributeDefinition(
      obj.id,
      obj.entityType,
      obj.uiType,
      obj.name,
      obj.slug,
      obj.description,
      obj.adminDocumentation,
      obj.filterable,
      obj.featured,
      obj.required,
      obj.isReadOnly,
      obj.isHidden,
      obj.hasVisualization,
      obj.showOnCreate,
      obj.isSubField,
      obj.min,
      obj.max,
      obj.allowsUserAddedValues,
      obj.valueList,
      obj.subFieldList,
      obj.workspaces,
      obj.createdBy,
      obj.createdOn,
      obj.updatedBy,
      obj.updatedOn,
      obj.isArchived,
      obj.hasOther,
      obj.includeInRatingCalculation
    );
    result.effectiveType = obj.effectiveType ?? obj.uiType;
    result.formulaExpression = obj.formulaExpression;
    result.formulaReturnType = obj.formulaReturnType;
    result.showCurrentValue = obj.showCurrentValue;
    return result;
  }

  static orderByNameCompareFn(def1: AttributeDefinition, def2: AttributeDefinition): number {
    if (def1.name > def2.name) return 1;
    if (def2.name > def1.name) return -1;
    return 0;
  }

  static getSearchValueType(item: AttributeDefinition): SearchValueType {
    // For calculated fields, effectiveType maps the formula's return type to the analogous
    // UIType ('ShortAnswer' for Text, 'DropDown' for Enumerated, etc.) so filter operators
    // match the materialized data shape.
    const effective = (item.effectiveType ?? item.uiType) as AttributeUiType;
    if (new AttributeUiTypeDetails(effective).isEnumerated) {
      return SearchValueType.MultiValue;
    }
    if (effective == AttributeUiType.LongAnswer || effective == AttributeUiType.ShortAnswer) {
      return SearchValueType.String;
    }
    if (effective == AttributeUiType.Number || effective == AttributeUiType.StarRating) {
      return SearchValueType.Number;
    }
    if (effective == AttributeUiType.Date) {
      return SearchValueType.Date;
    }
    if (effective == AttributeUiType.DateRange) {
      return SearchValueType.DateRange;
    }
    if (effective == AttributeUiType.Document || effective == AttributeUiType.EncryptedField) {
      return SearchValueType.EmptyOperator;
    }

    return null;
  }
}

/** mirrors the backend form-submission snapshot rule (ApplicationSubmitAggregateRoot.BuildSnapshot):
 *  only these questions ever have answers captured, so only they can show response data */
export function isSnapshotEligible(definition: {
  isReadOnly: boolean;
  isHidden: boolean;
  entityType: string;
}): boolean {
  return (
    !!definition &&
    !definition.isReadOnly &&
    !definition.isHidden &&
    [AttributeEntityType.Person, AttributeEntityType.Seat, AttributeEntityType.Team].includes(
      definition.entityType as AttributeEntityType
    )
  );
}
